<template>
  <div
    class="poll-results"
    :class="{ 'poll-dark': $vuetify.theme.dark, 'poll-light': !$vuetify.theme.dark }"
  >
    <template v-if="results">
      <h2 class="poll-results-title">{{ results.title }}</h2>
      <div class="poll-results-sub">
        <span class="poll-results-count">
          {{ voterCount }} {{ voterCount === 1 ? 'voter' : 'voters' }}<span v-if="results.status === 'closed'"> · closed</span>
        </span>
        <span v-if="options.length > 1" class="poll-filter-chips">
          <v-chip
            v-for="chip in filterChips"
            :key="chip.value"
            small
            class="poll-filter-chip"
            :class="{ 'poll-filter-chip--active': activeCategory === chip.value }"
            :outlined="activeCategory !== chip.value"
            @click="activeCategory = chip.value"
          >
            {{ chip.label }}
          </v-chip>
        </span>
      </div>

      <div v-if="visibleTracks.length === 0" class="poll-results-empty">
        {{ emptyMessage }}
      </div>

      <div v-else class="results-grid" :style="gridStyle" @mouseleave="hoveredId = null">
        <!-- Sticky, sortable header -->
        <div class="results-cell results-head results-track-head" :style="stickyStyle" @click="setSort('name')">
          Track <span class="sort-arrow">{{ sortArrow('name') }}</span>
        </div>
        <div v-if="anyComments" class="results-cell results-head results-comment-head" :style="stickyStyle"></div>
        <div
          v-for="opt in visibleOptions"
          :key="'head-' + opt.id"
          class="results-cell results-head results-num-head"
          :style="stickyStyle"
          @click="setSort('opt:' + opt.id)"
        >
          <span class="results-opt-label">{{ opt.label }}</span>
          <span class="sort-arrow">{{ sortArrow('opt:' + opt.id) }}</span>
        </div>
        <div v-if="showTotal" class="results-cell results-head results-num-head" :style="stickyStyle" @click="setSort('total')">
          Total <span class="sort-arrow">{{ sortArrow('total') }}</span>
        </div>

        <!-- One row per voted-for track. The active/hover highlight is applied to EVERY
             cell so it spans the whole row (visible under the numbers), not just the name. -->
        <template v-for="track in visibleTracks">
          <div
            :key="'name-' + track.id"
            :id="'trackID' + track.id"
            class="results-cell results-track-name"
            :class="rowClass(track)"
            @click="playRow(track)"
            @mouseenter="hoveredId = track.id"
          >
            {{ track.name }}
          </div>
          <div
            v-if="anyComments"
            :key="'comment-' + track.id"
            class="results-cell results-comment-cell"
            :class="rowClass(track)"
            @mouseenter="hoveredId = track.id"
          >
            <v-btn
              v-if="hasComments(track)"
              icon
              small
              class="results-comment-btn"
              :title="commentTitle(track)"
              @click.stop="openThread(track.id)"
            >
              <v-icon small>mdi-comment-text</v-icon>
            </v-btn>
          </div>
          <div
            v-for="opt in visibleOptions"
            :key="track.id + '-' + opt.id"
            class="results-cell results-num"
            :class="rowClass(track)"
            @mouseenter="hoveredId = track.id"
          >
            <span class="results-count">{{ countFor(track, opt.id) }}</span>
          </div>
          <div
            v-if="showTotal"
            :key="'total-' + track.id"
            class="results-cell results-num results-total"
            :class="rowClass(track)"
            @mouseenter="hoveredId = track.id"
          >
            <span class="results-count">{{ track.poll_total || 0 }}</span>
          </div>
        </template>
      </div>
    </template>

    <div v-else class="poll-results-empty">Loading results…</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import scrollToTrack from '@/mixins/scrollToTrack';

export default {
  name: 'PollResultsTable',

  mixins: [scrollToTrack],

  props: {
    stickyTop: { type: Number, default: 0 },
  },

  data() {
    return {
      sortKey: 'total', // 'name' | 'total' | 'opt:<id>'
      sortDir: 'desc',
      hoveredId: null,
    };
  },

  computed: {
    ...mapGetters({
      results: 'poll/results',
      sortedRecordings: 'playlist/sortedRecordings',
      currentTrackDetails: 'player/currentTrackDetails',
      isPlaying: 'player/isPlaying',
    }),

    options() {
      return (this.results && this.results.options) || [];
    },

    tracks() {
      return (this.results && this.results.tracks) || [];
    },

    voterCount() {
      return (this.results && this.results.voter_count) || 0;
    },

    // The comment column (and its header) only appear when at least one voted-for track
    // actually has comments — otherwise it would be a wasted empty column.
    anyComments() {
      return this.tracks.some(t => this.hasComments(t));
    },

    // ── Category filter ──
    // The selected category lives in the URL (?category=<label>) so a filtered view is
    // shareable and survives a refresh. 'all' (no param, or an unknown label) shows every
    // column; a single category collapses the grid to just that column (no Total).
    filterChips() {
      return [{ value: 'all', label: 'All' }, ...this.options.map(o => ({ value: o.id, label: o.label }))];
    },

    activeCategory: {
      get() {
        const raw = (this.$route && this.$route.query && this.$route.query.category) || '';
        const cat = raw.toString().trim().toLowerCase();
        if (!cat) return 'all';
        const opt = this.options.find(o => (o.label || '').toLowerCase() === cat);
        return opt ? opt.id : 'all';
      },
      set(value) {
        const query = { ...this.$route.query };
        if (value === 'all') {
          delete query.category;
        } else {
          const opt = this.options.find(o => o.id === value);
          if (opt) query.category = opt.label;
          else delete query.category;
        }
        // replace (not push) so filter toggles don't pile up in browser history;
        // swallow the redundant-navigation rejection when the query is unchanged.
        this.$router.replace({ query }).catch(() => {});
      },
    },

    activeOption() {
      if (this.activeCategory === 'all') return null;
      return this.options.find(o => o.id === this.activeCategory) || null;
    },

    // Single-category view shows only that column; otherwise every category.
    visibleOptions() {
      return this.activeOption ? [this.activeOption] : this.options;
    },

    // The Total column only makes sense when every category is visible.
    showTotal() {
      return this.activeCategory === 'all';
    },

    gridStyle() {
      // Category + total columns hug their labels so headers never truncate, but stay at
      // least --results-col-w wide so the grid lines up evenly. The fixed comment column
      // between the track name and the first category is only present when some track has
      // comments (see anyComments).
      const col = 'minmax(var(--results-col-w), max-content)';
      const parts = ['minmax(0, 1fr)'];
      if (this.anyComments) parts.push('var(--results-col-w)');
      this.visibleOptions.forEach(() => parts.push(col));
      if (this.showTotal) parts.push(col);
      return { gridTemplateColumns: parts.join(' ') };
    },

    stickyStyle() {
      return { top: this.stickyTop + 'px' };
    },

    sortedTracks() {
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const key = this.sortKey;
      return [...this.tracks].sort((a, b) => {
        if (key === 'name') {
          return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()) * dir;
        }
        let av;
        let bv;
        if (key === 'total') {
          av = a.poll_total || 0;
          bv = b.poll_total || 0;
        } else {
          const oid = parseInt(key.slice(4), 10);
          av = this.countFor(a, oid);
          bv = this.countFor(b, oid);
        }
        if (av !== bv) return (av - bv) * dir;
        // tie-break: total desc, then name asc
        const at = a.poll_total || 0;
        const bt = b.poll_total || 0;
        if (at !== bt) return bt - at;
        return (a.name || '').localeCompare(b.name || '');
      });
    },

    // Rows actually rendered: in single-category view, hide tracks with no votes in that
    // category so the list reads as "what people picked for <category>".
    visibleTracks() {
      if (this.activeCategory === 'all') return this.sortedTracks;
      return this.sortedTracks.filter(t => this.countFor(t, this.activeCategory) > 0);
    },

    emptyMessage() {
      if (this.tracks.length === 0) return 'No votes have been cast yet.';
      if (this.activeOption) return `No ${this.activeOption.label} votes yet.`;
      return 'No votes have been cast yet.';
    },
  },

  watch: {
    // Keep the playing track in view as it auto-advances (or on login
    // continuity) — same behavior as the regular playlist. The player advances
    // in catalog order, so the highlighted row may sit anywhere in the
    // vote-sorted leaderboard; scroll to wherever it lands.
    currentTrackDetails(newTrack, oldTrack) {
      if (!newTrack || !oldTrack || !this.isPlaying) return;
      this.scheduleScrollToTrack(newTrack.id);
    },

    // When the category filter changes (including the initial value parsed from the URL),
    // sort by whatever metric is visible: the chosen category, or Total in the all view.
    activeCategory: {
      immediate: true,
      handler(value) {
        this.sortKey = value === 'all' ? 'total' : 'opt:' + value;
        this.sortDir = 'desc';
      },
    },
  },

  methods: {
    ...mapActions({
      selectAudioTrack: 'player/selectTrack',
    }),

    countFor(track, optionId) {
      const pc = (track.poll_counts || []).find(c => c.option_id === optionId);
      return pc ? pc.count : 0;
    },

    hasComments(track) {
      return (track.comment_count || 0) > 0;
    },

    commentTitle(track) {
      const n = track.comment_count || 0;
      return `View ${n} comment${n === 1 ? '' : 's'}`;
    },

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = key === 'name' ? 'asc' : 'desc';
      }
    },

    sortArrow(key) {
      if (this.sortKey !== key) return '';
      return this.sortDir === 'asc' ? '▲' : '▼';
    },

    isActive(trackID) {
      return this.currentTrackDetails && this.currentTrackDetails.id === trackID;
    },

    rowClass(track) {
      return {
        'results-track-active': this.isActive(track.id),
        'results-row-hover': this.hoveredId === track.id,
      };
    },

    openThread(recordingId) {
      this.$store.dispatch('comments/openThread', recordingId);
    },

    playRow(track) {
      // Play through the normal player: find the track's index in the loaded catalog.
      const index = this.sortedRecordings.findIndex(r => r.id === track.id);
      if (index >= 0) {
        this.selectAudioTrack({ trackID: track.id, index });
      }
    },
  },
};
</script>

<style scoped>
.poll-results {
  --results-col-w: 80px;
  width: 100%;
  margin-bottom: 150px;
}

.poll-results-title {
  text-align: center;
  font-size: 1.2rem;
  font-weight: 200;
  margin: 0;
  padding: 10px 30px 0 30px;
  word-break: break-word;
}

.poll-results-sub {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 2px 10px;
  margin-bottom: 16px;
}

.poll-results-count {
  font-size: 0.85rem;
  opacity: 0.7;
  white-space: nowrap;
}

.poll-filter-chips {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.poll-filter-chip {
  cursor: pointer;
}

/* Selected filter: high-contrast inverted fill so the label stays readable in both themes
   (Vuetify's default filled-chip text was too light on the active background in dark mode). */
.poll-dark .poll-filter-chip.poll-filter-chip--active {
  background-color: #fff !important;
  color: #121212 !important;
}

.poll-light .poll-filter-chip.poll-filter-chip--active {
  background-color: #121212 !important;
  color: #fff !important;
}

.results-grid {
  display: grid;
  width: 100%;
}

.results-cell {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--v-tertiary-base);
  min-height: 48px;
  box-sizing: border-box;
}

.results-head {
  position: sticky;
  z-index: 4;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  user-select: none;
}

.poll-light .results-head { background: #fff; }
.poll-dark .results-head { background: #121212; }

.results-track-head {
  padding-left: 16px;
}

.results-num-head {
  justify-content: center;
  text-align: center;
  padding: 4px 8px;
}

.results-comment-head {
  cursor: default;
}

.results-comment-cell {
  justify-content: center;
}

.results-comment-btn {
  margin: 0 !important;
}

.results-opt-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sort-arrow {
  font-size: 0.7em;
  margin-left: 2px;
}

.results-track-name {
  padding: 8px 8px 8px 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  min-width: 0;
}

.results-track-active {
  background-color: var(--v-tertiary-base);
}

@media (hover: hover) {
  .results-row-hover {
    background-color: var(--v-tertiary-base);
  }
}

.results-num {
  justify-content: center;
  align-items: center;
  padding: 4px;
}

.results-count {
  font-variant-numeric: tabular-nums;
}

.poll-results-empty {
  text-align: center;
  margin-top: 40px;
  opacity: 0.7;
}

@media only screen and (max-width: 599px) {
  .poll-results { --results-col-w: 72px; }
  .results-cell { min-height: 44px; }
  .results-head { font-size: 0.7rem; }
  .results-track-name { font-size: 14px; padding-left: 12px; }
  .results-track-head { padding-left: 12px; }
}

@media only screen and (max-width: 360px) {
  .poll-results { --results-col-w: 64px; }
  .results-head { font-size: 0.6rem; }
}
</style>
